import type { ReactNode } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  Button,
  Skeleton,
} from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { DescList, DescItem, monoValue } from '@/pages/services/components/DescList';
import {
  useWaapAccessLogDetail,
  type WaapAccessLogArgs,
  type WaapAccessLogDetail,
  type WaapTableColumn,
  type WaapTableRow,
} from '@/api/waap';
import styles from './LogDetailModal.module.css';

/** '—' for empty/missing; arrays joined; everything else stringified. Mirrors the
 *  vendor Log Detail modal, which shows "-" for blank fields. */
function s(v: unknown): string {
  if (v == null || v === '') return '—';
  if (Array.isArray(v)) return v.length ? v.map(String).join(', ') : '—';
  return String(v);
}

/** A field only the provider's detail lookup carries — marked, not blanked, so
 *  it can't be misread as "the log recorded nothing here". */
const NEEDS_LOOKUP = <span className={styles.needsLookup}>Needs provider lookup</span>;

/** Vendor 0/1 flag → "Y"/"N" (blank → "—"). */
function yn(v: unknown): string {
  return v == null || v === '' ? '—' : Number(v) ? 'Y' : 'N';
}

/**
 * The vendor keys the sections below render by name. Everything else in the
 * record falls through to "Raw record" — the access-log body is a pass-through
 * `Record<string, unknown>` (the BE forwards it untouched), so a fixed field list
 * silently drops whatever the vendor added to explain a decision. Anyone
 * investigating a block needs to see those fields, not have them hidden.
 */
const RENDERED_KEYS = new Set([
  'timestamp', 'hostname', 'http_host', 'src_ip', 'x_forwarded_for', 'country', 'province',
  'attack_type', 'request_uuid', 'is_abnormal', 'status', 'action', 'user_agent', 'referer',
  'path', 'args', 'protocol', 'inject_attack_type', 'inject_rule_id', 'inject_payload',
  'manual_rule_id', 'in_blacklist_ip', 'ubm_browser_type', 'ubm_browser_version', 'ubm_platform',
]);

/** Render any value for the raw list: objects/arrays as compact JSON. */
function raw(v: unknown): string {
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

/** Record entries not covered by the named sections, blanks dropped, key-sorted. */
function extraEntries(d: WaapAccessLogDetail): [string, unknown][] {
  return Object.entries(d)
    .filter(([k, v]) => !RENDERED_KEYS.has(k) && v != null && v !== '')
    .sort(([a], [b]) => a.localeCompare(b));
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h4 className={styles.sectionTitle}>{title}</h4>
      {children}
    </section>
  );
}

/**
 * Log Detail modal — opened by clicking a row in the Threat Investigation
 * "Details" table. Fetches the full per-request access-log record and lays it out
 * in the vendor's sections (summary + Request / Threat / Device info). The record
 * is the vendor's flat access-log object, so fields are read by their upstream key.
 */
export function LogDetailModal({
  domainId,
  args,
  row,
  columns,
  open,
  onOpenChange,
}: {
  domainId: string | undefined;
  args: WaapAccessLogArgs | null;
  /** The Details row that was clicked, with the columns it was rendered from —
   *  what the modal falls back to when the provider's lookup gives nothing. */
  row?: WaapTableRow | null;
  columns?: WaapTableColumn[];
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const { data, isLoading, isError, error } = useWaapAccessLogDetail(domainId, args, open);
  // 502 = our gateway relaying the provider's failure; surface it so a report
  // carries something the provider can trace.
  const upstreamStatus = (error as { response?: { status?: number } } | null)?.response?.status;
  // Row values for the fallback, keyed the way the BE slugs its column labels
  // ("Origin Response Time" → origin_response_time).
  const r = (key: string) => s(row?.[key]);
  const hasRow = !!row && (columns ?? []).length > 0;
  const d: WaapAccessLogDetail = data ?? {};
  const region = [d.country, d.province].filter((x) => x != null && x !== '').map(String).join(', ');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Log Detail</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {isLoading ? (
            <Skeleton height={360} />
          ) : isError || data == null ? (
            // The provider's own portal gets a 500 out of this lookup too
            // (verified against waap2 with the exact request it sends), so don't
            // dead-end on it: the clicked row already carries every field the
            // Details table queried, which is most of what the modal would show.
            <div className={styles.sections}>
              <Alert tone="warning" title="Showing the log row">
                The provider&apos;s log-detail service returned an error for this request
                {upstreamStatus ? ` (HTTP ${upstreamStatus})` : ''}, so the fingerprint, PII and
                injection findings it adds are missing. Everything the access log itself recorded
                is below.
              </Alert>
              {hasRow ? (
                // Same blocks, same labels and same order as the full record
                // below, so the modal doesn't change shape between the two
                // states — only the values the row can't supply are marked.
                <>
                  <DescList>
                    <DescItem label="Time">{r('time')}</DescItem>
                    <DescItem label="Website">{r('host')}</DescItem>
                    <DescItem label="Source IP">
                      <span style={monoValue}>{r('source_ip')}</span>
                    </DescItem>
                    <DescItem label="Region">{r('region')}</DescItem>
                    <DescItem label="Attack Type">{r('attack_type')}</DescItem>
                  </DescList>

                  <Section title="Request Info">
                    <DescList>
                      <DescItem label="Request ID">
                        <span style={monoValue}>{r('request_id')}</span>
                      </DescItem>
                      <DescItem label="Is Malicious Request">{NEEDS_LOOKUP}</DescItem>
                      <DescItem label="Status">{r('status')}</DescItem>
                      <DescItem label="HTTP Method">{r('http_method')}</DescItem>
                      <DescItem label="User Agent">{r('user_agent')}</DescItem>
                      <DescItem label="Referer">{r('referer')}</DescItem>
                      <DescItem label="Path">{r('path')}</DescItem>
                      <DescItem label="Argument">{r('args')}</DescItem>
                      <DescItem label="HTTP Protocol">{NEEDS_LOOKUP}</DescItem>
                      <DescItem label="Origin Status">{r('origin_status')}</DescItem>
                      <DescItem label="Origin IP">
                        <span style={monoValue}>{r('origin_ip')}</span>
                      </DescItem>
                      <DescItem label="Origin Response Time">{r('origin_response_time')}</DescItem>
                    </DescList>
                  </Section>

                  <Section title="Threat Info">
                    <DescList>
                      <DescItem label="Inject Attack Type">{r('inject_attack_type')}</DescItem>
                      <DescItem label="Inject Rule ID">
                        <code
                          className={styles.ruleId}
                          title="WAF rule identifier — the engine rule that matched this request"
                        >
                          {r('inject_rule_id')}
                        </code>
                      </DescItem>
                      <DescItem label="Inject Payload">{r('inject_payload')}</DescItem>
                      <DescItem label="Manual Rule ID">
                        <code
                          className={styles.ruleId}
                          title="WAF rule identifier — the custom/manual rule that triggered this action"
                        >
                          {r('manual_rule_id')}
                        </code>
                      </DescItem>
                      <DescItem label="IP Whitelisted">{yn(row?.in_whitelist_ip)}</DescItem>
                      <DescItem label="IP Blacklisted">{NEEDS_LOOKUP}</DescItem>
                    </DescList>
                  </Section>

                  <Section title="Device Info">
                    <DescList>
                      <DescItem label="Browser">{NEEDS_LOOKUP}</DescItem>
                      <DescItem label="Browser Version">{NEEDS_LOOKUP}</DescItem>
                      <DescItem label="Device Type">{NEEDS_LOOKUP}</DescItem>
                    </DescList>
                  </Section>
                </>
              ) : (
                <DescList>
                  <DescItem label="Request ID">
                    <span style={monoValue}>{s(args?.requestUuid)}</span>
                  </DescItem>
                  <DescItem label="Website">{s(args?.hostname)}</DescItem>
                  <DescItem label="Time">
                    {args?.timestampMs ? new Date(args.timestampMs).toLocaleString() : '—'}
                  </DescItem>
                </DescList>
              )}
            </div>
          ) : (
            <div className={styles.sections}>
              <DescList>
                <DescItem label="Time">{s(d.timestamp)}</DescItem>
                <DescItem label="Website">{s(d.hostname ?? d.http_host)}</DescItem>
                <DescItem label="Source IP">
                  <span style={monoValue}>{s(d.src_ip ?? d.x_forwarded_for)}</span>
                </DescItem>
                <DescItem label="Region">{region || '—'}</DescItem>
                <DescItem label="Attack Type">{s(d.attack_type)}</DescItem>
              </DescList>

              <Section title="Request Info">
                <DescList>
                  <DescItem label="Request ID">
                    <span style={monoValue}>{s(d.request_uuid)}</span>
                  </DescItem>
                  <DescItem label="Is Malicious Request">{yn(d.is_abnormal)}</DescItem>
                  <DescItem label="Status">{s(d.status)}</DescItem>
                  <DescItem label="HTTP Method">{s(d.action)}</DescItem>
                  <DescItem label="User Agent">{s(d.user_agent)}</DescItem>
                  <DescItem label="Referer">{s(d.referer)}</DescItem>
                  <DescItem label="Path">{s(d.path)}</DescItem>
                  <DescItem label="Argument">{s(d.args)}</DescItem>
                  <DescItem label="HTTP Protocol">{s(d.protocol)}</DescItem>
                </DescList>
              </Section>

              <Section title="Threat Info">
                <DescList>
                  <DescItem label="Inject Attack Type">{s(d.inject_attack_type)}</DescItem>
                  <DescItem label="Inject Rule ID">
                    <code
                      className={styles.ruleId}
                      title="WAF rule identifier — the engine rule that matched this request"
                    >
                      {s(d.inject_rule_id)}
                    </code>
                  </DescItem>
                  <DescItem label="Inject Payload">{s(d.inject_payload)}</DescItem>
                  <DescItem label="Manual Rule ID">
                    <code
                      className={styles.ruleId}
                      title="WAF rule identifier — the custom/manual rule that triggered this action"
                    >
                      {s(d.manual_rule_id)}
                    </code>
                  </DescItem>
                  <DescItem label="IP Blacklisted">{yn(d.in_blacklist_ip)}</DescItem>
                </DescList>
              </Section>

              <Section title="Device Info">
                <DescList>
                  <DescItem label="Browser">{s(d.ubm_browser_type)}</DescItem>
                  <DescItem label="Browser Version">{s(d.ubm_browser_version)}</DescItem>
                  <DescItem label="Device Type">{s(d.ubm_platform)}</DescItem>
                </DescList>
              </Section>

              <RawRecord entries={extraEntries(d)} />
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Every field of the access-log record the named sections above don't show,
 * collapsed by default. When the summary fields don't explain a decision — a 403
 * next to "Attack Type: OK" and "Inject Rule ID: -1" — this is where the vendor's
 * own reason lives, so it has to be reachable instead of dropped on the floor.
 */
function RawRecord({ entries }: { entries: [string, unknown][] }) {
  if (entries.length === 0) return null;
  return (
    <details className={styles.raw}>
      <summary className={styles.rawSummary}>
        Raw record · {entries.length} more field{entries.length === 1 ? '' : 's'}
      </summary>
      <dl className={styles.rawList}>
        {entries.map(([key, value]) => (
          <div key={key} className={styles.rawRow}>
            <dt className={styles.rawKey}>{key}</dt>
            <dd className={styles.rawValue}>{raw(value)}</dd>
          </div>
        ))}
      </dl>
    </details>
  );
}
